import React, { useLayoutEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Slider, LinearProgress } from '@material-ui/core';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import ReplayIcon from '@material-ui/icons/Replay';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  player: {
    position: 'relative',

    '& .playerUI': {
      position: 'absolute',
      zIndex: '10',
      bottom: 0,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.5rem 1rem',
      width: '100%',
      background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.5))',
      color: theme.palette.secondary.main,
      opacity: 0,
      transition: 'opacity 0.33s ease',
      '& > *:not(:last-child)': {
        marginRight: '0.75rem',
      },
    },
    '&:hover .playerUI': {
      opacity: 1,
    },
  },
  volumeSlider: {
    width: '6rem',
  },
  progress: {
    cursor: 'pointer',
    flexGrow: 1,
    height: '0.75rem',
  },
  fullScreenButton: {
    [theme.breakpoints.down('mobile')]: {
      display: 'none',
    },
  },
  timer: {
    width: '7ch',
    textAlign: 'center',
  },
}));

VodControls.propTypes = {
  children: PropTypes.node.isRequired,
  playerRef: PropTypes.any.isRequired,
  minTime: PropTypes.number,
  maxTime: PropTypes.number,
};

function getProgress(currentTime, minTime, maxTime) {
  if (currentTime <= minTime) return 0;
  if (currentTime >= maxTime) return 100;

  currentTime -= minTime;
  maxTime -= minTime;

  return (currentTime / maxTime) * 100;
}

export default function VodControls({ children, playerRef, minTime, maxTime }) {
  const player = playerRef?.current;
  const classes = useStyles();

  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(true);
  const [duration, setDuration] = useState(1);

  const fullScreen = useFullScreenHandle();

  const togglePause = () => {
    if (player.paused) player.play();
    else player.pause();
  };

  const restart = () => {
    player.currentTime = minTime || 0;
    player.play();
  };

  const toggleMute = () => {
    player.muted = !player.muted;
    setMuted(player.muted);
  };

  const toggleFullscreen = () => {
    if (fullScreen.active) fullScreen.exit();
    else fullScreen.enter();
  };

  const onChangeVolume = (event, volume) => {
    player.volume = volume;
  };

  const onVolumeChange = () => void setVolume(player.volume);
  const onTimeUpdate = () => void setCurrentTime(player.currentTime);
  const onDurationChange = () => void setDuration(player.duration);
  const onPause = () => void setPaused(true);
  const onPlay = () => void setPaused(false);

  const onClickProgress = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickLocalX = (event.pageX - rect.x) / rect.width;
    const min = minTime || 0;
    const max = maxTime || duration || 1;

    player.currentTime = clickLocalX + min + (max - min) * clickLocalX;
  };

  useLayoutEffect(() => {
    if (player) {
      player.controls = false;
      if (minTime) {
        player.currentTime = minTime;
      }

      player.addEventListener('timeupdate', onTimeUpdate);
      player.addEventListener('volumechange', onVolumeChange);
      player.addEventListener('pause', onPause);
      player.addEventListener('play', onPlay);
      player.addEventListener('durationchange', onDurationChange);
    }

    return () => {
      if (!player) return;

      player.removeEventListener('timeupdate', onTimeUpdate);
      player.removeEventListener('volumechange', onVolumeChange);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('play', onPlay);
      player.removeEventListener('durationchange', onDurationChange);
    };
  }, [player, minTime, maxTime]); //eslint-disable-line react-hooks/exhaustive-deps

  let roundedTime = Math.round(currentTime) - (minTime || 0);
  let minutes = Math.floor(roundedTime / 60);
  let seconds = (roundedTime - minutes * 60).toString().padStart(2, '0');
  return (
    <FullScreen handle={fullScreen}>
      <div className={classes.player}>
        {children}

        {player && (
          <div className="playerUI">
            {currentTime === (maxTime || duration || 1) ? (
              <IconButton onClick={restart}>
                <ReplayIcon />
              </IconButton>
            ) : (
              <IconButton onClick={togglePause}>
                {paused ? <PlayArrowIcon /> : <PauseIcon />}
              </IconButton>
            )}
            <IconButton onClick={toggleMute}>
              {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>

            <Slider
              min={0}
              max={1}
              step={0.01}
              value={volume}
              className={classes.volumeSlider}
              onChange={onChangeVolume}
            />
            <div className={classes.timer}>{`${minutes}:${seconds}`}</div>
            <LinearProgress
              variant="determinate"
              value={getProgress(currentTime, minTime || 0, maxTime || duration || 1)}
              className={classes.progress}
              onClick={onClickProgress}
            />

            <IconButton
              className={classes.fullScreenButton}
              style={fullScreen.active ? { display: 'block!important' } : {}}
              onClick={toggleFullscreen}
            >
              <FullscreenIcon />
            </IconButton>
          </div>
        )}
      </div>
    </FullScreen>
  );
}
